import { useState, useEffect } from 'react'
import { useTranslation } from '@/hooks/useTypedTranslation'
import { 
  useCreateMultilingualBlog, 
  useUpdateMultilingualBlog,
  MultilingualBlogItem 
} from '@/hooks/useMultilingualBlogs'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Loader2, X, Globe, Save, Languages, FileText, Eye, Edit3, Upload, ImageIcon, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

interface BlogTranslations {
  zh: {
    title: string
    description: string
    content: string
  }
  en: {
    title: string
    description: string
    content: string
  }
}

interface BlogData {
  slug: string
  image: string
  tags: string[]
  is_published: boolean
  sort_order: number
}

interface MultilingualBlogEditorProps {
  blog?: MultilingualBlogItem | null
  onClose: () => void
  onSuccess?: () => void
}

// Image uploader component
function ImageUploader({
  value,
  onChange,
  label
}: {
  value: string
  onChange: (url: string) => void
  label: string
}) {
  const [uploading, setUploading] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  
  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file')
      return
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB')
      return
    }
    
    setUploading(true)
    try {
      const fileExt = file.name.split('.').pop()
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`
      const filePath = `covers/${fileName}`
      
      const { error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file)
      
      if (uploadError) throw uploadError
      
      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath)
      
      onChange(publicUrl)
      toast.success('Image uploaded successfully')
    } catch (error: any) {
      console.error('Upload failed:', error)
      toast.error(error.message || 'Failed to upload image')
    } finally {
      setUploading(false)
    }
  }
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleUpload(file)
  }
  
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleUpload(file)
  }
  
  const handleRemove = () => {
    onChange('')
  }
  
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      
      {value ? (
        <div className="relative group">
          <img 
            src={value} 
            alt="Cover" 
            className="w-full h-40 object-cover rounded-lg border"
          />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
            <label className="cursor-pointer">
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                onChange={handleFileChange}
                disabled={uploading}
              />
              <Button variant="secondary" size="sm" asChild>
                <span>
                  <Upload className="h-4 w-4 mr-1" />
                  Replace
                </span>
              </Button>
            </label>
            <Button variant="destructive" size="sm" onClick={handleRemove}>
              <Trash2 className="h-4 w-4 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <label
          className={`
            flex flex-col items-center justify-center w-full h-40 
            border-2 border-dashed rounded-lg cursor-pointer
            transition-colors
            ${dragActive 
              ? 'border-primary bg-primary/5' 
              : 'border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/50'
            }
            ${uploading ? 'pointer-events-none opacity-50' : ''}
          `}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
          onDragLeave={() => setDragActive(false)}
          onDrop={handleDrop}
        >
          <input 
            type="file" 
            accept="image/*" 
            className="hidden" 
            onChange={handleFileChange}
            disabled={uploading}
          />
          {uploading ? (
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          ) : (
            <>
              <ImageIcon className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Drag & drop or click to upload
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Max 5MB • JPG, PNG, GIF, WebP
              </p>
            </>
          )}
        </label>
      )}
    </div>
  )
}

// Markdown content editor with preview
function MarkdownEditor({ 
  value, 
  onChange, 
  placeholder,
  label
}: { 
  value: string
  onChange: (value: string) => void
  placeholder: string
  label: string
}) {
  const [mode, setMode] = useState<'edit' | 'preview' | 'split'>('edit')

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <div className="flex items-center gap-1 bg-muted rounded-md p-0.5">
          <Button 
            variant={mode === 'edit' ? 'secondary' : 'ghost'} 
            size="sm" 
            className="h-7 px-2 text-xs"
            onClick={() => setMode('edit')}
          >
            <Edit3 className="h-3 w-3 mr-1" />
            Edit
          </Button>
          <Button 
            variant={mode === 'split' ? 'secondary' : 'ghost'} 
            size="sm" 
            className="h-7 px-2 text-xs"
            onClick={() => setMode('split')}
          >
            Split
          </Button>
          <Button 
            variant={mode === 'preview' ? 'secondary' : 'ghost'} 
            size="sm" 
            className="h-7 px-2 text-xs"
            onClick={() => setMode('preview')}
          >
            <Eye className="h-3 w-3 mr-1" />
            Preview
          </Button>
        </div>
      </div>

      {mode === 'edit' && (
        <Textarea
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          rows={14}
          className="font-mono text-sm"
        />
      )}

      {mode === 'preview' && (
        <ScrollArea className="h-[350px] border rounded-md p-4 bg-background">
          {value ? (
            <article className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {value}
              </ReactMarkdown>
            </article>
          ) : (
            <p className="text-muted-foreground text-sm italic">No content to preview</p>
          )}
        </ScrollArea>
      )}

      {mode === 'split' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Textarea
              value={value}
              onChange={e => onChange(e.target.value)}
              placeholder={placeholder}
              rows={14}
              className="font-mono text-sm h-[350px]"
            />
          </div>
          <ScrollArea className="h-[350px] border rounded-md p-4 bg-background">
            {value ? (
              <article className="prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {value}
                </ReactMarkdown>
              </article>
            ) : (
              <p className="text-muted-foreground text-sm italic">No content to preview</p>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  )
}

export function MultilingualBlogEditor({ blog, onClose, onSuccess }: MultilingualBlogEditorProps) {
  const { t } = useTranslation()
  
  const createBlog = useCreateMultilingualBlog()
  const updateBlog = useUpdateMultilingualBlog()
  
  const isEditing = !!blog
  
  // Blog base data
  const [blogData, setBlogData] = useState<BlogData>({
    slug: '',
    image: '',
    tags: [],
    is_published: false,
    sort_order: 0
  })
  
  // Translations
  const [translations, setTranslations] = useState<BlogTranslations>({
    zh: { title: '', description: '', content: '' },
    en: { title: '', description: '', content: '' }
  })
  
  const [loading, setLoading] = useState(false)
  const [loadingTranslations, setLoadingTranslations] = useState(false)
  
  // Load existing blog data
  useEffect(() => {
    if (blog) {
      setBlogData({
        slug: blog.slug,
        image: blog.image || '',
        tags: blog.tags || [],
        is_published: blog.is_published,
        sort_order: blog.sort_order
      })
      
      // Load both translations
      loadTranslations(blog.id)
    }
  }, [blog])
  
  const loadTranslations = async (blogId: string) => {
    setLoadingTranslations(true)
    try {
      const { data, error } = await supabase
        .from('blog_translations')
        .select('*')
        .eq('blog_id', blogId)
      
      if (error) throw error
      
      const zhTrans = data?.find(t => t.language === 'zh')
      const enTrans = data?.find(t => t.language === 'en')
      
      setTranslations({
        zh: {
          title: zhTrans?.title || '',
          description: zhTrans?.description || '',
          content: zhTrans?.content || ''
        },
        en: {
          title: enTrans?.title || '',
          description: enTrans?.description || '',
          content: enTrans?.content || ''
        }
      })
    } catch (error) {
      console.error('Failed to load translations:', error)
      toast.error('Failed to load translations')
    } finally {
      setLoadingTranslations(false)
    }
  }
  
  const handleSave = async () => {
    // Validation
    if (!blogData.slug.trim()) {
      toast.error('Blog slug is required')
      return
    }
    if (!translations.zh.title.trim() || !translations.en.title.trim()) {
      toast.error('Title is required in both languages')
      return
    }
    
    setLoading(true)
    try {
      if (isEditing) {
        await updateBlog.mutateAsync({
          id: blog!.id,
          blog: {
            slug: blogData.slug,
            image: blogData.image || null,
            tags: blogData.tags,
            is_published: blogData.is_published,
            published_at: blogData.is_published ? new Date().toISOString() : null,
            sort_order: blogData.sort_order
          },
          translations: {
            zh: translations.zh,
            en: translations.en
          }
        })
        toast.success('Blog updated successfully')
      } else {
        await createBlog.mutateAsync({
          blog: {
            slug: blogData.slug,
            image: blogData.image || undefined,
            tags: blogData.tags,
            is_published: blogData.is_published,
            published_at: blogData.is_published ? new Date().toISOString() : undefined,
            sort_order: blogData.sort_order
          },
          translations: {
            zh: translations.zh,
            en: translations.en
          }
        })
        toast.success('Blog created successfully')
      }
      onSuccess?.()
      onClose()
    } catch (error: any) {
      console.error('Failed to save blog:', error)
      toast.error(error.message || 'Failed to save blog')
    } finally {
      setLoading(false)
    }
  }
  
  const updateTranslation = (lang: 'zh' | 'en', field: keyof BlogTranslations['zh'], value: string) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value }
    }))
  }
  
  const parseArrayInput = (value: string): string[] => {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return value.split(',').map(s => s.trim()).filter(Boolean)
    }
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FileText className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-bold">
            {isEditing ? 'Edit Multilingual Blog' : 'Create Multilingual Blog'}
          </h2>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      {loadingTranslations ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Base Blog Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="slug">Blog Slug *</Label>
                    <Input
                      id="slug"
                      value={blogData.slug}
                      onChange={e => setBlogData(prev => ({ ...prev, slug: e.target.value }))}
                      placeholder="my-blog-post"
                    />
                    <p className="text-xs text-muted-foreground mt-1">URL path for the blog post</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="sort_order">Sort Order</Label>
                    <Input
                      id="sort_order"
                      type="number"
                      value={blogData.sort_order}
                      onChange={e => setBlogData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                </div>
                
                <ImageUploader
                  value={blogData.image}
                  onChange={(url) => setBlogData(prev => ({ ...prev, image: url }))}
                  label="Cover Image"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="tags">Tags (comma-separated)</Label>
                  <Input
                    id="tags"
                    defaultValue={blogData.tags.join(', ')}
                    onChange={e => setBlogData(prev => ({ ...prev, tags: parseArrayInput(e.target.value) }))}
                    placeholder="tech, tutorial, react"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    id="is_published"
                    checked={blogData.is_published}
                    onCheckedChange={checked => setBlogData(prev => ({ ...prev, is_published: checked }))}
                  />
                  <Label htmlFor="is_published">Published</Label>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Multilingual Content - Side by Side */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Languages className="h-4 w-4" />
                Multilingual Content
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="side-by-side" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="side-by-side">Side by Side</TabsTrigger>
                  <TabsTrigger value="chinese">Chinese Only</TabsTrigger>
                  <TabsTrigger value="english">English Only</TabsTrigger>
                </TabsList>
                
                <TabsContent value="side-by-side" className="mt-0">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Chinese Column */}
                    <div className="space-y-4 p-4 border rounded-lg bg-red-50/30 dark:bg-red-950/10">
                      <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium mb-4">
                        <span className="text-lg">🇨🇳</span>
                        <span>中文 (Chinese)</span>
                      </div>
                      
                      <div>
                        <Label>标题 (Title) *</Label>
                        <Input
                          value={translations.zh.title}
                          onChange={e => updateTranslation('zh', 'title', e.target.value)}
                          placeholder="博客标题"
                        />
                      </div>
                      
                      <div>
                        <Label>描述 (Description)</Label>
                        <Textarea
                          value={translations.zh.description}
                          onChange={e => updateTranslation('zh', 'description', e.target.value)}
                          placeholder="博客简介..."
                          rows={3}
                        />
                      </div>
                      
                      <MarkdownEditor
                        value={translations.zh.content}
                        onChange={value => updateTranslation('zh', 'content', value)}
                        placeholder="博客内容 (支持 Markdown)..."
                        label="内容 (Content)"
                      />
                    </div>
                    
                    {/* English Column */}
                    <div className="space-y-4 p-4 border rounded-lg bg-blue-50/30 dark:bg-blue-950/10">
                      <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium mb-4">
                        <span className="text-lg">🇺🇸</span>
                        <span>English</span>
                      </div>
                      
                      <div>
                        <Label>Title *</Label>
                        <Input
                          value={translations.en.title}
                          onChange={e => updateTranslation('en', 'title', e.target.value)}
                          placeholder="Blog title"
                        />
                      </div>
                      
                      <div>
                        <Label>Description</Label>
                        <Textarea
                          value={translations.en.description}
                          onChange={e => updateTranslation('en', 'description', e.target.value)}
                          placeholder="Blog description..."
                          rows={3}
                        />
                      </div>
                      
                      <MarkdownEditor
                        value={translations.en.content}
                        onChange={value => updateTranslation('en', 'content', value)}
                        placeholder="Blog content (Markdown supported)..."
                        label="Content"
                      />
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="chinese" className="mt-0">
                  <div className="space-y-4 p-4 border rounded-lg bg-red-50/30 dark:bg-red-950/10 max-w-3xl">
                    <div className="flex items-center gap-2 text-red-600 dark:text-red-400 font-medium mb-4">
                      <span className="text-lg">🇨🇳</span>
                      <span>中文 (Chinese)</span>
                    </div>
                    
                    <div>
                      <Label>标题 (Title) *</Label>
                      <Input
                        value={translations.zh.title}
                        onChange={e => updateTranslation('zh', 'title', e.target.value)}
                        placeholder="博客标题"
                      />
                    </div>
                    
                    <div>
                      <Label>描述 (Description)</Label>
                      <Textarea
                        value={translations.zh.description}
                        onChange={e => updateTranslation('zh', 'description', e.target.value)}
                        placeholder="博客简介..."
                        rows={3}
                      />
                    </div>
                    
                    <MarkdownEditor
                      value={translations.zh.content}
                      onChange={value => updateTranslation('zh', 'content', value)}
                      placeholder="博客内容 (支持 Markdown)..."
                      label="内容 (Content)"
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="english" className="mt-0">
                  <div className="space-y-4 p-4 border rounded-lg bg-blue-50/30 dark:bg-blue-950/10 max-w-3xl">
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 font-medium mb-4">
                      <span className="text-lg">🇺🇸</span>
                      <span>English</span>
                    </div>
                    
                    <div>
                      <Label>Title *</Label>
                      <Input
                        value={translations.en.title}
                        onChange={e => updateTranslation('en', 'title', e.target.value)}
                        placeholder="Blog title"
                      />
                    </div>
                    
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={translations.en.description}
                        onChange={e => updateTranslation('en', 'description', e.target.value)}
                        placeholder="Blog description..."
                        rows={3}
                      />
                    </div>
                    
                    <MarkdownEditor
                      value={translations.en.content}
                      onChange={value => updateTranslation('en', 'content', value)}
                      placeholder="Blog content (Markdown supported)..."
                      label="Content"
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
          
          {/* Actions */}
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Blog' : 'Create Blog'}
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
